import { useEffect, useRef, useState } from "react";
import { motion, useInView } from "framer-motion";

// Results / Proof Numbers — restrained editorial credibility strip.
// Only verified official Riversol proof points are used:
//   • 115,000+ happy customers (official)
//   • 15M facial images informing the Skin Analyzer (official)
//   • 20+ years of clinical dermatology (riversol.com/pages/about-us:
//     "more than 20 years of clinical dermatology")
// Numbers are deliberately smaller than a typical landing-page stat,
// echoing the PDP's measured typography. Count-up runs once on enter.
const STATS = [
  {
    value: 115000,
    suffix: "+",
    label: "Happy customers",
    hint: "Dermatologist-developed skincare trusted worldwide.",
  },
  {
    value: 15,
    suffix: "M",
    label: "Facial images",
    hint: "Informing the Riversol Virtual Skin Analyzer.",
  },
  {
    value: 20,
    suffix: "+ yrs",
    label: "Clinical dermatology",
    hint: "Dr. Jason Rivers' practice behind every formula.",
  },
];

const formatNumber = (n, decimals = 0) => {
  if (decimals > 0) return n.toFixed(decimals);
  if (n >= 1000) return Math.round(n).toLocaleString("en-US");
  return Math.round(n).toString();
};

const CountUp = ({ target, decimals = 0, active }) => {
  const [n, setN] = useState(0);
  const startedRef = useRef(false);
  useEffect(() => {
    if (!active || startedRef.current) return;
    startedRef.current = true;
    const prefersReduced =
      typeof window !== "undefined" &&
      window.matchMedia("(prefers-reduced-motion: reduce)").matches;
    if (prefersReduced) {
      setN(target);
      return;
    }
    const duration = 1400;
    const start = performance.now();
    let raf;
    const tick = (t) => {
      const p = Math.min((t - start) / duration, 1);
      const eased = 1 - Math.pow(1 - p, 3); // ease-out, no overshoot/bounce
      setN(target * eased);
      if (p < 1) raf = requestAnimationFrame(tick);
    };
    raf = requestAnimationFrame(tick);
    return () => raf && cancelAnimationFrame(raf);
  }, [active, target]);
  return <>{formatNumber(n, decimals)}</>;
};

export const NumberProof = () => {
  const ref = useRef(null);
  const inView = useInView(ref, { once: true, margin: "-120px" });
  return (
    <section
      ref={ref}
      className="bg-riversol-paper py-24 lg:py-32"
      data-testid="home-number-proof"
    >
      <div className="max-w-[1400px] mx-auto px-6 lg:px-10">
        <motion.div
          initial={{ opacity: 0 }}
          animate={inView ? { opacity: 1 } : {}}
          transition={{ duration: 0.6 }}
          className="hidden font-mono text-[10px] tracking-[0.28em] uppercase text-neutral-500"
        >
          Proven over time
        </motion.div>
        <motion.h2
          initial={{ opacity: 0, y: 24 }}
          animate={inView ? { opacity: 1, y: 0 } : {}}
          transition={{ duration: 0.7, ease: [0.16, 1, 0.3, 1] }}
          className="mt-4 max-w-[720px] text-[32px] sm:text-[38px] lg:text-[46px] leading-[1.06] tracking-[-0.02em] text-neutral-900"
        >
          Two decades of dermatology,{" "}
          <span className="italic text-riversol-blue">measured in results.</span>
        </motion.h2>

        <div className="mt-14 lg:mt-20 grid grid-cols-1 sm:grid-cols-3 gap-y-10 sm:gap-x-10 lg:gap-x-16">
          {STATS.map((s, i) => (
            <motion.div
              key={s.label}
              initial={{ opacity: 0, y: 24 }}
              animate={inView ? { opacity: 1, y: 0 } : {}}
              transition={{ duration: 0.8, ease: [0.16, 1, 0.3, 1], delay: 0.15 + i * 0.1 }}
              className="relative sm:pl-8 sm:first:pl-0 sm:border-l sm:first:border-l-0 border-neutral-300/70"
              data-testid={`stat-${i}`}
            >
              <div className="font-mono tabular-nums text-5xl md:text-6xl lg:text-7xl leading-none tracking-tighter text-neutral-900 whitespace-nowrap">
                <CountUp target={s.value} active={inView} />
                <span className="font-mono text-riversol-blue text-2xl md:text-3xl lg:text-4xl align-top ml-1">
                  {s.suffix}
                </span>
              </div>
              <div className="mt-4 font-mono text-[11px] tracking-[0.2em] uppercase text-neutral-900">
                {s.label}
              </div>
              <p className="mt-2 text-[13px] leading-relaxed text-neutral-500 max-w-[240px]">
                {s.hint}
              </p>
            </motion.div>
          ))}
        </div>
      </div>
    </section>
  );
};

export default NumberProof;
